import { useEffect, useState } from "react";
import { Bitcoin, Copy, Check, Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface WalletEntry {
  key: string;
  label: string;
  address: string;
  hint: string;
}

const WALLET_KEYS: { key: string; label: string; hint: string }[] = [
  { key: "donation_wallet_btc", label: "Bitcoin (BTC)", hint: "BTC network" },
  { key: "donation_wallet_eth", label: "Ethereum (ETH)", hint: "ERC-20" },
  { key: "donation_wallet_usdt", label: "Tether (USDT)", hint: "TRC-20 / ERC-20" },
];

export function DonationWidget() {
  const [wallets, setWallets] = useState<WalletEntry[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", WALLET_KEYS.map((w) => w.key));
      if (cancelled) return;
      const map = Object.fromEntries((data ?? []).map((r: any) => [r.key, r.value ?? ""]));
      setWallets(
        WALLET_KEYS.map((w) => ({
          key: w.key,
          label: w.label,
          hint: w.hint,
          address: (map[w.key] || "").trim(),
        })).filter((w) => w.address.length > 0)
      );
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (wallets.length === 0) return null;

  const copy = async (key: string, address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(key);
      toast({ title: "Copied", description: "Wallet address copied to clipboard" });
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  return (
    <div className="glass-card p-5 max-w-2xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <Heart className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Support the developer</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        This platform is built and maintained by an independent developer. Crypto donations help cover infrastructure costs and keep the free tier running.
      </p>
      <div className="space-y-2">
        {wallets.map((w) => (
          <div
            key={w.key}
            className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border/40"
          >
            <Bitcoin className="w-4 h-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-foreground">
                {w.label} <span className="text-muted-foreground/60">· {w.hint}</span>
              </div>
              <div className="text-xs font-mono text-muted-foreground truncate">{w.address}</div>
            </div>
            <button
              onClick={() => copy(w.key, w.address)}
              className="p-2 rounded-md hover:bg-muted/50 transition-colors shrink-0"
              aria-label={`Copy ${w.label} address`}
            >
              {copied === w.key ? (
                <Check className="w-4 h-4 text-primary" />
              ) : (
                <Copy className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
